import { useState, useCallback, useEffect } from "react";
import { AxiosError } from "axios";
// import apiClient from "../config/api";
import type { UserWithLikes, UserLike } from "../types";
import { useGeneralApiCall } from "@/services/useGeneralApiCall";

interface UseUserAnalyticsReturn {
  usersWithLikes: UserWithLikes[];
  selectedUserLikes: UserLike[];
  selectedUserId: string | null;
  isLoading: boolean;
  error: string | null;
  fetchUsersWithLikes: () => Promise<void>;
  fetchUserLikes: (userId: string) => Promise<void>;
  setSelectedUserId: (userId: string | null) => void;
  refreshData: () => void;
}

/**
 * Custom hook for user analytics operations following SOLID principles
 * Single Responsibility: Manages user analytics data and operations
 * Open/Closed: Extensible for additional analytics features
 * Dependency Inversion: Depends on apiClient abstraction
 */
export const useUserAnalytics = (): UseUserAnalyticsReturn => {
  const [usersWithLikes, setUsersWithLikes] = useState<UserWithLikes[]>([]);
  const [selectedUserLikes, setSelectedUserLikes] = useState<UserLike[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { getApi } = useGeneralApiCall();

  // Fetch all users with at least one like
  const fetchUsersWithLikes = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      //   const response = await apiClient.get<UserWithLikes[]>(
      //     "/style/v1/like/all-users-with-at-least-one-like/"
      //   );

      const response = await getApi<UserWithLikes[]>(
        "api/style/v1/like/all-users-with-at-least-one-like/"
      );
      const users = response.data || [];

      // Validate and filter users
      // const validUsers = users.filter(
      //   (user): user is UserWithLikes =>
      //     user &&
      //     typeof user === "object" &&
      //     "user_id" in user &&
      //     "Email" in user &&
      //     "Name" in user &&
      //     "likes_count" in user &&
      //     Boolean(user.user_id) &&
      //     Boolean(user.Email) &&
      //     Boolean(user.Name) &&
      //     typeof user.likes_count === "number"
      // );

      setUsersWithLikes(users);
    } catch (err) {
      const errorMessage =
        err instanceof AxiosError
          ? err.response?.data?.message || "Failed to fetch users with likes"
          : "An unexpected error occurred";
      setError(errorMessage);
      console.error("Error fetching users with likes:", err);
      setUsersWithLikes([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch likes for a specific user
  const fetchUserLikes = useCallback(async (userId: string) => {
    if (!userId) return;

    try {
      setIsLoading(true);
      setError(null);

      console.log("Fetching likes for user:", userId);
      //   const response = await apiClient.get<UserLike[]>(
      //     `/style/v1/like/single-user-likes/${userId}/`
      //   );
      const response = await getApi<UserLike[]>(
        `api/style/v1/like/single-user-likes/${userId}/`
      );
      console.log("Raw API response:", response.data);
      const likes = response.data || [];

      // Validate and filter likes
      const validLikes = likes.filter(
        (like): like is UserLike =>
          like &&
          typeof like === "object" &&
          "like_id" in like &&
          "style_id" in like &&
          "name" in like &&
          Boolean(like.like_id) &&
          Boolean(like.style_id) &&
          Boolean(like.name)
      );

      console.log("Valid likes after filtering:", validLikes);
      setSelectedUserLikes(validLikes);
      setSelectedUserId(userId);
    } catch (err) {
      const errorMessage =
        err instanceof AxiosError
          ? err.response?.data?.message || "Failed to fetch user likes"
          : "An unexpected error occurred";
      setError(errorMessage);
      console.error("Error fetching user likes:", err);
      setSelectedUserLikes([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Refresh all data
  const refreshData = useCallback(() => {
    fetchUsersWithLikes();
    if (selectedUserId) {
      fetchUserLikes(selectedUserId);
    }
  }, [fetchUsersWithLikes, fetchUserLikes, selectedUserId]);

  // Initial data fetch
  useEffect(() => {
    fetchUsersWithLikes();
  }, [fetchUsersWithLikes]);

  return {
    usersWithLikes,
    selectedUserLikes,
    selectedUserId,
    isLoading,
    error,
    fetchUsersWithLikes,
    fetchUserLikes,
    setSelectedUserId,
    refreshData,
  };
};
